import { useState } from "react";
import { useAccount, type StaffWorkspace } from "../../app/session";
import { Dialog } from "../../components/ui";
import { t } from "../../lib/sprache";
import { useOnboardingPrefs } from "../creator/Einfuehrung";
import { WEBSITE_URL } from "../../app/nav";

/**
 * Demo-Video (Inhaber 03.11.2026): Beim Einstieg ins Agentur-Portal steht oben ein Hinweis auf das Demo-Video, bis die Person ihn
 * ausblendet. Die Wahl liegt in ihren Onboarding-Einstellungen (user_preferences), gilt also auf allen Geräten. Das Video läuft in
 * einem Dialog im Portal; ohne hinterlegte Adresse (DEMO_VIDEO.url leer) erscheint nichts, damit nie ein toter Link steht.
 */
export const DEMO_VIDEO: { url: string; poster?: string; minuten: number } = { url: `${WEBSITE_URL}/video/demo-2026-11-02-hell-1080p.mp4`, minuten: 3 };
/** Im dunklen Schema die dunkle Fassung (data-theme am <html>, lib/theme.ts) */
export function demoVideoUrl(theme: string | undefined): string {
  return theme === "dark" ? DEMO_VIDEO.url.replace("-hell-", "-dunkel-") : DEMO_VIDEO.url;
}

/** Sichtbar, solange eine Adresse da ist, die Einstellungen geladen sind, nicht eingebettet, kein Creator-Konto und noch nicht ausgeblendet */
export function demoVideoSichtbar(onboarding: { guides?: Record<string, boolean | undefined> }, url: string, ready: boolean, embedded: boolean, creatorKonto: boolean): boolean {
  return !!url && ready && !embedded && !creatorKonto && !onboarding.guides?.demo_video;
}

export function DemoVideoHinweis({ ws }: { ws: StaffWorkspace }) {
  const { account, session } = useAccount();
  const userId = account?.session.userId ?? session?.userId ?? null;
  const { onboarding, ready, save } = useOnboardingPrefs(userId);
  const [offen, setOffen] = useState(false);
  const [weg, setWeg] = useState(false);
  const embedded = typeof document !== "undefined" && document.documentElement.dataset.embed === "true";
  if (weg || !demoVideoSichtbar(onboarding as { guides?: Record<string, boolean | undefined> }, DEMO_VIDEO.url, ready, embedded, !!ws.isCreator)) return null;
  const ausblenden = () => {
    setWeg(true);
    void save({ guides: { demo_video: true } as never }).catch(() => undefined);
  };
  const n = DEMO_VIDEO.minuten;
  return (
    <div className="content tk-rahmen-hinweise tk-noprint">
      <div className="tk-leiste tk-start-banner tk-demo-video" role="note">
        <span className="tk-start-banner-stand">{t("Demo-Video", "Demo video")}</span>
        <div className="tk-start-banner-text">
          <strong>{t(`In ${n} Minuten durchs Portal`, `Through the portal in ${n} minutes`)}</strong>
          <small>{t("Das Video zeigt den Weg von der Markenanfrage bis zur Auszahlung an deine Creator.", "The video shows the way from the brand request to the payout to your creators.")}</small>
        </div>
        <button type="button" className="primary" onClick={() => setOffen(true)}>{t("Video ansehen", "Watch the video")}</button>
        <button type="button" onClick={ausblenden}>{t("Ausblenden", "Hide")}</button>
      </div>
      <Dialog open={offen} onClose={() => setOffen(false)} title={t("Demo-Video", "Demo video")}>
        {/* Das Video kommt von der eigenen Ablage (R2, EU), kein Drittanbieter-Player, keine Cookies */}
        <video className="tk-demo-video-player" controls preload="metadata" src={demoVideoUrl(document.documentElement.dataset.theme)} poster={DEMO_VIDEO.poster}>
          {/* Untertitel als abschaltbare Spur (im Bild stehen sie schon), für Vorlesen und Suche */}
          <track kind="subtitles" srcLang="de" label="Deutsch" src={`${WEBSITE_URL}/video/demo-2026-11-02.de.vtt`} />
        </video>
        <p className="muted"><a href={`${WEBSITE_URL}/demo-video`} target="_blank" rel="noreferrer">{t("Video auf der Website öffnen", "Open the video on the website")}</a></p>
      </Dialog>
    </div>
  );
}
